import React, { useEffect, useState } from 'react'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import InputGroup from 'react-bootstrap/InputGroup'
import Button from 'react-bootstrap/Button'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import Dropdown from 'react-bootstrap/Dropdown'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Alert from 'react-bootstrap/Alert'
import Badge from 'react-bootstrap/Badge'
import { toast } from 'react-toastify'
import Paginacion from 'components/Paginacion'
import api from 'api/api'

import './publicaciones.css'
import NuevaPublicacion from './NuevaPublicacion'
import VerPublicacion from './VerPublicacion'
import EditarPublicacion from './EditarPublicacion'
import EliminarPublicacion from './EliminarPublicacion'
import TopHeader from 'components/TopHeader'
import { DEFAULT_FETCH_CUANTOS } from 'config/general'
import { debounce } from 'utils/debounce'

// Constantes
export const TIPO_VIDEO = 'video'
export const TIPO_GALERIA = 'galeria'

function Publicaciones(props) {
    const [fetchDeACuantos, setFetchDeACuantos] = useState(DEFAULT_FETCH_CUANTOS)
    const [numResultados, setNumResultados] = useState(0)
    const [paginaAct, setPaginaAct] = useState(1)
    const [loading, setLoading] = useState(false)

    const [busqueda, setBusqueda] = useState('')
    const [filtroBusqueda, setFiltroBusqueda] = useState('')
    const [filtroTipo, setFiltroTipo] = useState('todos')
    const [publicaciones, setPublicaciones] = useState([])
    const [publicacionActual, setPublicacionActual] = useState(null)
    const [queMostrar, setQueMostrar] = useState('')
    const [etiquetas, setEtiquetas] = useState([])
    const [config, setConfig] = useState({})

    const opcionesCuantosMostrar = [10, 25, 50]

    // Obtener thumbnail de YouTube
    const getYouTubeThumbnail = (videoUrl) => {
        if (!videoUrl) return null

        const patterns = [
            /youtube\.com\/watch\?v=([^&]+)/,
            /youtube\.com\/embed\/([^?]+)/,
            /youtu\.be\/([^?]+)/,
            /youtube\.com\/v\/([^?]+)/
        ]

        for (const pattern of patterns) {
            const match = videoUrl.match(pattern)
            if (match) {
                return `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`
            }
        }

        // Si es directamente el ID
        if (videoUrl.length === 11) {
            return `https://img.youtube.com/vi/${videoUrl}/hqdefault.jpg`
        }

        return null
    }

    // Obtener imagen para mostrar en listado
    const getImagenListado = (publicacion) => {
        // Prioridad: imagen destacada > thumbnail YouTube > primera imagen galeria
        if (publicacion.imagenDestacada && publicacion.imagenDestacada.url) {
            return process.env.REACT_APP_API_URL + publicacion.imagenDestacada.url
        }

        if (publicacion.tipo === TIPO_VIDEO && publicacion.videoUrl) {
            return getYouTubeThumbnail(publicacion.videoUrl)
        }

        if (publicacion.tipo === TIPO_GALERIA && publicacion.imagenes && publicacion.imagenes.length > 0) {
            return process.env.REACT_APP_API_URL + publicacion.imagenes[0].url
        }

        return null
    }

    const refreshConfig = async () => {
        try {
            const { datos } = await api.get('p/config-publicaciones')
            setEtiquetas(datos.etiquetas || [])
            setConfig(datos)
        } catch (error) {
            console.error('Error al cargar configuracion:', error)
        }
    }

    const refreshPublicaciones = async () => {
        try {
            setLoading(true)
            const { datos } = await api.post('p/ver-publicaciones', {
                cuantos: fetchDeACuantos,
                desde: paginaAct === 1 ? 0 : (paginaAct - 1) * fetchDeACuantos,
                filtros: {
                    titulo: [filtroBusqueda],
                    etiqueta: [filtroBusqueda],
                    tipo: filtroTipo
                }
            })

            setPublicaciones(datos.publicaciones || [])
            setNumResultados(datos.numResultados || 0)
        } catch (error) {
            toast.error('Error de conexion')
            console.error(error)
        } finally {
            setLoading(false)
        }
    }

    // Verificar si es borrador
    const esBorrador = (pub) => {
        return pub.borrador === '1' || pub.borrador === 1 || pub.borrador === true
    }

    const handleBusqueda = (value) => {
        setFiltroBusqueda(value || '')
    }

    const debouncedFiltroBusqueda = React.useCallback(
        debounce(handleBusqueda, 400),
        []
    )

    useEffect(() => {
        debouncedFiltroBusqueda(busqueda)
    }, [busqueda])

    useEffect(() => {
        if (paginaAct === 1) {
            refreshPublicaciones()
        } else {
            setPaginaAct(1)
        }
    }, [filtroBusqueda, fetchDeACuantos, filtroTipo])

    useEffect(() => {
        if (!loading) {
            refreshPublicaciones()
        }
    }, [paginaAct])

    useEffect(() => {
        refreshConfig()
        refreshPublicaciones()
    }, [])

    const handleAction = (publicacion, cual) => {
        setPublicacionActual(publicacion)
        setQueMostrar(cual)
    }

    // Crear nueva publicacion
    const nuevaPublicacion = async (data) => {
        try {
            const { msg } = await api.post('a/publicaciones', data)
            refreshConfig()
            refreshPublicaciones()
            setQueMostrar('')
            toast.success(msg)
        } catch (error) {
            console.error(error)
            toast.error('Error al crear la publicacion')
        }
    }

    // Editar publicacion
    const editarPublicacion = async (data) => {
        try {
            const { msg } = await api.put('a/publicaciones/' + data.id, data)
            refreshConfig()
            refreshPublicaciones()
            setQueMostrar('')
            toast.success(msg)
        } catch (error) {
            console.error(error)
            toast.error('Error al editar la publicacion')
        }
    }

    // Eliminar publicacion
    const eliminarPublicacion = async (id) => {
        try {
            const { msg } = await api.delete('a/publicaciones/' + id)
            refreshConfig()
            refreshPublicaciones()
            setQueMostrar('')
            toast.success(msg)
        } catch (error) {
            console.error(error)
            toast.error('Error al eliminar la publicacion')
        }
    }

    return (
        <Container fluid id="publicaciones" className="vh-100">
            <TopHeader
                iconName="newspaper"
                text="Publicaciones"
            />

            {/* Barra de busqueda */}
            <Row className="d-flex no-gutters">
                <Col className="mt-2">
                    <Form>
                        <Row id="searchbar" className="justify-content-between align-items-center">
                            <Col sm={6} md={3}>
                                <small>
                                    <span className="d-none d-sm-inline-block">Mostrar de a</span>
                                    <Form.Group style={{ display: 'inline-block' }}>
                                        <Form.Select
                                            size="sm"
                                            onChange={(e) => setFetchDeACuantos(e.target.value)}
                                            className="mx-1"
                                        >
                                            {opcionesCuantosMostrar.map((o) => (
                                                <option value={o} key={`mostrar-${o}`}>{o}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                    <span className="ms-2">publicaciones</span>
                                </small>
                            </Col>
                            <Col md={5} lg={4}>
                                <Form.Group controlId="txtBusqueda">
                                    <InputGroup>
                                        <Form.Control
                                            autoFocus
                                            value={busqueda}
                                            onChange={(e) => setBusqueda(e.target.value)}
                                            type="text"
                                            placeholder="Buscar por titulo o etiqueta"
                                            aria-label="Buscar"
                                        />
                                        <InputGroup.Text>
                                            <i className="fa fa-search"></i>
                                        </InputGroup.Text>
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Row>

            {/* Acciones y filtros */}
            <Row className="mb-3">
                <Col className="d-flex gap-2 flex-wrap align-items-center">
                    <Button
                        variant="primary"
                        onClick={() => setQueMostrar('nuevo')}
                        className="mt-2"
                    >
                        <i className="fa fa-plus me-1"></i> Nueva Publicacion
                    </Button>

                    {/* Filtro por tipo */}
                    <Form.Group className="mt-2 ms-auto">
                        <Form.Select
                            size="sm"
                            value={filtroTipo}
                            onChange={(e) => setFiltroTipo(e.target.value)}
                        >
                            <option value="todos">Todos los tipos</option>
                            <option value="video">Videos</option>
                            <option value="galeria">Galerias</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>

            {/* Lista de publicaciones */}
            <Row>
                <Col sm={12} lg={8}>
                    {loading ? (
                        <div className="text-center my-4">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : publicaciones.length === 0 ? (
                        <Alert variant="info">
                            <i className="fa fa-info-circle me-2"></i>
                            No se encontraron publicaciones
                        </Alert>
                    ) : (
                        <div className="publicaciones-list">
                            {publicaciones.map((pub) => {
                                const borrador = esBorrador(pub)
                                const imagenUrl = getImagenListado(pub)

                                return (
                                    <div
                                        key={pub.id}
                                        className={`publicacion-item ${borrador ? 'borrador' : ''}`}
                                    >
                                        {/* Thumbnail */}
                                        <div className="publicacion-thumbnail">
                                            {imagenUrl ? (
                                                <img src={imagenUrl} alt={pub.titulo} />
                                            ) : (
                                                <div className="thumbnail-placeholder">
                                                    <i className={`fa fa-${pub.tipo === TIPO_VIDEO ? 'play-circle' : 'images'}`}></i>
                                                </div>
                                            )}
                                            {/* Badge de tipo */}
                                            <span className={`tipo-badge ${pub.tipo}`}>
                                                <i className={`fa fa-${pub.tipo === TIPO_VIDEO ? 'play' : 'images'} me-1`}></i>
                                                {pub.tipo === TIPO_VIDEO ? 'Video' : 'Galeria'}
                                            </span>
                                        </div>

                                        {/* Datos */}
                                        <div className="publicacion-data">
                                            <div className="publicacion-titulo">
                                                {pub.titulo}
                                                {borrador && (
                                                    <Badge bg="warning" className="ms-2">Borrador</Badge>
                                                )}
                                            </div>
                                            <div className="publicacion-etiquetas">
                                                {pub.etiquetas && pub.etiquetas.map((etq, idx) => (
                                                    <Badge key={idx} bg="primary" className="me-1">
                                                        {etq.nombre}
                                                    </Badge>
                                                ))}
                                            </div>
                                            {pub.copete && (
                                                <div className="publicacion-copete">
                                                    {pub.copete.substring(0, 100)}
                                                    {pub.copete.length > 100 ? '...' : ''}
                                                </div>
                                            )}
                                        </div>

                                        {/* Fecha */}
                                        <div className="publicacion-fecha">
                                            {pub.fecha ? pub.fecha.substring(0, 10) : ''}
                                        </div>

                                        {/* Acciones */}
                                        <DropdownButton
                                            as={ButtonGroup}
                                            title={<i className="fa fa-ellipsis-v"></i>}
                                            drop="end"
                                            variant="link"
                                            className="publicacion-actions"
                                        >
                                            <Dropdown.Item onClick={() => handleAction(pub, 'ver')}>
                                                <i className="fa fa-eye me-2"></i> Ver
                                            </Dropdown.Item>
                                            <Dropdown.Item onClick={() => handleAction(pub, 'editar')}>
                                                <i className="fa fa-pencil me-2"></i> Editar
                                            </Dropdown.Item>
                                            <Dropdown.Divider />
                                            <Dropdown.Item
                                                onClick={() => handleAction(pub, 'eliminar')}
                                                className="text-danger"
                                            >
                                                <i className="fa fa-trash me-2"></i> Eliminar
                                            </Dropdown.Item>
                                        </DropdownButton>
                                    </div>
                                )
                            })}
                        </div>
                    )}

                    <Paginacion
                        pagina={paginaAct}
                        numPaginas={Math.ceil(numResultados / fetchDeACuantos) || 1}
                        setPagina={setPaginaAct}
                    />
                </Col>

                {/* Leyenda */}
                <Col sm={12} lg={4}>
                    <div className="leyenda-card">
                        <h5><i className="fa fa-info-circle me-2"></i>Tipos de Publicacion</h5>
                        <div className="leyenda-item">
                            <span className="leyenda-icon video">
                                <i className="fa fa-play"></i>
                            </span>
                            <span><strong>Video:</strong> Video de YouTube + texto</span>
                        </div>
                        <div className="leyenda-item">
                            <span className="leyenda-icon galeria">
                                <i className="fa fa-images"></i>
                            </span>
                            <span><strong>Galeria:</strong> Hasta 10 fotos + texto</span>
                        </div>
                        <hr />
                        <div className="leyenda-item">
                            <span className="leyenda-color borrador"></span>
                            <span>Publicaciones no publicadas (borrador)</span>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Modales y Paneles */}
            {queMostrar === 'nuevo' && (
                <NuevaPublicacion
                    etiquetasExistentes={etiquetas}
                    config={config}
                    guardar={nuevaPublicacion}
                    setQueMostrar={setQueMostrar}
                />
            )}

            {publicacionActual && queMostrar === 'ver' && (
                <VerPublicacion
                    publicacion={publicacionActual}
                    setQueMostrar={setQueMostrar}
                />
            )}

            {publicacionActual && queMostrar === 'editar' && (
                <EditarPublicacion
                    publicacionOriginal={publicacionActual}
                    etiquetasExistentes={etiquetas}
                    config={config}
                    setQueMostrar={setQueMostrar}
                    guardar={editarPublicacion}
                />
            )}

            {publicacionActual && queMostrar === 'eliminar' && (
                <EliminarPublicacion
                    publicacion={publicacionActual}
                    setQueMostrar={setQueMostrar}
                    eliminar={eliminarPublicacion}
                />
            )}
        </Container>
    )
}

export default Publicaciones
